import React, { useState, useEffect } from 'react';
import ReportePagosYPopularidad from './ReportePagosYPopularidad';
import ReporteProductosmasvendidos from './ReporteProductosmasvendidos';
import { IP } from '../configuracion.js';
import '../styles/ReporteVentas.css';

const BASE_API_URL = `${IP}/tavpos/backend`;

const ReporteVentas = ({ property = "propiedad2" }) => {
  const [activeReport, setActiveReport] = useState("ventas");
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupedData, setGroupedData] = useState({});
  const [apiStatus, setApiStatus] = useState({
    reachable: null,
    testResponse: null
  });
  const [numberFormat, setNumberFormat] = useState('en-US'); // Default fallback
  const [currency, setCurrency] = useState('USD'); // Default fallback

  const buildApiUrl = (endpoint = "lectura_ventas_depto.php") => 
    `${BASE_API_URL}/${endpoint}?property=${encodeURIComponent(property)}`;

  const checkApiEndpoint = async () => {
    const url = buildApiUrl();
    try {
      console.log("Checking API endpoint:", url);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 
          'X-Property': property,
          'Content-Type': 'application/json'
        },
        mode: 'cors'
      });
      console.log("checkApiEndpoint response status:", response.status);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      console.log("checkApiEndpoint response data:", data);
      setApiStatus({ reachable: true, testResponse: data });
      return true;
    } catch (error) {
      console.error("checkApiEndpoint error:", error.message);
      setApiStatus({ reachable: false, testResponse: null });
      return false;
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(buildApiUrl("get_timezone.php"));
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al cargar configuración (${response.status}): ${errorText}`);
      }
      const data = await response.json();
      console.log("Datos de get_timezone.php:", JSON.stringify(data, null, 2));
      if (!data.success || !data.data) {
        throw new Error(data.message || "No se pudo obtener la configuración.");
      }
      setNumberFormat(data.data.numberFormat || 'en-US');
      setCurrency(data.data.currency || 'USD');
      console.log("Configuración establecida - numberFormat:", data.data.numberFormat, "currency:", data.data.currency);
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      setError(error.message || "Error al cargar configuración. Usando en-US y USD como fallback.");
    }
  };

  const processData = (data) => {
    if (!Array.isArray(data)) return {};
    
    const grouped = {};
    
    data.forEach(item => {
      const depto = item.departamento || 'Sin departamento';
      if (!grouped[depto]) {
        grouped[depto] = {
          totalVentas: 0,
          cantidadTotal: 0,
          productos: []
        };
      }
      
      const cantidad = Number(item.cantidad) || 0;
      const total = Number(item.total) || 0;
      
      grouped[depto].totalVentas += total;
      grouped[depto].cantidadTotal += cantidad;
      grouped[depto].productos.push({
        nombre: item.nombre || 'Sin nombre',
        cantidad,
        total,
        fecha_control: item.fecha_control || 'N/A'
      });
    });
    
    return grouped;
  };

  const formatCurrency = (amount) => 
    new Intl.NumberFormat(numberFormat, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const formatDate = (date) =>
    date && date !== 'N/A'
      ? new Date(date).toLocaleString(numberFormat)
      : 'N/A';

  const fetchVentas = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("Fetching ventas...");
      if (!(await checkApiEndpoint())) {
        throw new Error("API no accesible. Verifica tu conexión de red.");
      }

      const response = await fetch(buildApiUrl(), {
        method: "GET",
        headers: { 
          "X-Property": property,
          "Content-Type": "application/json"
        },
        mode: 'cors'
      });
      console.log("fetchVentas response status:", response.status);
      if (!response.ok) throw new Error(`Error ${response.status}`);
      
      const data = await response.json();
      console.log("fetchVentas response data:", data);
      if (!data.success) throw new Error(data.message || "Error en respuesta");

      setVentas(data.data || []);
      setGroupedData(processData(data.data || []));
    } catch (err) {
      console.error("fetchVentas error:", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig().then(() => {
      if (activeReport === "ventas") fetchVentas();
    });
  }, [activeReport, property]);

  return (
    <div className="reportes-container">
      <div className="reportes-header">
        <h2><i className="bi bi-graph-up"></i> Reportes de Ventas</h2>
      </div>
      
      <div className="reportes-botones-container">
        <button 
          className={`reporte-accion-boton ${activeReport === "ventas" ? "seleccionado" : ""}`} 
          onClick={() => setActiveReport("ventas")}
        >
          <i className="bi bi-bar-chart"></i> Reporte Venta Depto
        </button>
        <button 
          className={`reporte-accion-boton ${activeReport === "popularidad" ? "seleccionado" : ""}`} 
          onClick={() => setActiveReport("popularidad")}
        >
          <i className="bi bi-credit-card"></i> Reporte Pagos
        </button>
        <button 
          className={`reporte-accion-boton ${activeReport === "masvendidos" ? "seleccionado" : ""}`} 
          onClick={() => setActiveReport("masvendidos")}
        >
          <i className="bi bi-trophy"></i> Más Vendidos
        </button>
      </div>

      {apiStatus.reachable !== true && (
        <div className="reporte-mensaje cargando">
          <p>
            <strong>Estado del API:</strong> 
            {apiStatus.reachable === false ? " ❌ Inaccesible" : " ⌛ Verificando..."}
          </p>
        </div>
      )}

      <div className="reportes-contenido">
        {activeReport === "ventas" ? (
          <div className="reporte-seccion">
            <div className="reporte-seccion-header">
              <h3><i className="bi bi-cash-stack"></i> Reporte de Ventas por Departamento</h3>
              {loading && <span className="reporte-cargando">Cargando...</span>}
            </div>
            
            {error && (
              <div className="reporte-mensaje error">
                <i className="bi bi-exclamation-triangle"></i> {error}
              </div>
            )}

            {!loading && !error && Object.keys(groupedData).length > 0 && (
              <div className="reporte-departamentos">
                {Object.entries(groupedData).map(([departamento, datos]) => (
                  <div key={departamento} className="reporte-departamento">
                    <div className="reporte-departamento-header">
                      <div className="reporte-departamento-titulo">
                        <i className="bi bi-shop"></i> 
                        <h4>{departamento}</h4>
                      </div>
                      <div className="reporte-departamento-totales">
                        <span className="reporte-total-cantidad">
                          <i className="bi bi-box-seam"></i> {datos.cantidadTotal} productos
                        </span>
                        <span className="reporte-total-ventas">
                          <i className="bi bi-cash-coin"></i> {formatCurrency(datos.totalVentas)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="reporte-productos-list">
                      {datos.productos.map((producto, idx) => (
                        <div key={idx} className="reporte-producto-item">
                          <div className="reporte-producto-nombre">
                            {producto.nombre}
                          </div>
                          <div className="reporte-producto-detalle">
                            <div className="reporte-producto-cantidad">
                              <i className="bi bi-123"></i> {producto.cantidad}
                            </div>
                            <div className="reporte-producto-total">
                              <i className="bi bi-currency-dollar"></i> {formatCurrency(producto.total)}
                            </div>
                            <div className="reporte-producto-fecha">
                              <i className="bi bi-calendar-check"></i> {formatDate(producto.fecha_control)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && !error && Object.keys(groupedData).length === 0 && (
              <div className="reporte-mensaje exito">
                <i className="bi bi-info-circle"></i> No hay datos de ventas disponibles para mostrar.
              </div>
            )}
          </div>
        ) : activeReport === "masvendidos" ? (
          <div className="reporte-seccion">
            <h3><i className="bi bi-trophy"></i> Productos Más Vendidos</h3>
            <ReporteProductosmasvendidos property={property} />
          </div>
        ) : (
          <div className="reporte-seccion">
            <h3><i className="bi bi-credit-card"></i> Reporte de Pagos y Popularidad</h3>
            <ReportePagosYPopularidad property={property} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReporteVentas;